/**
 * Interventie photos for ONE task, as the field worker sees them: upload, view,
 * delete. Shared by the Interventie shell and the installer shell (a tagged task
 * shows it next to the collectors), so whoever is on the repair documents it.
 *
 * The server keys a new photo on the task's FASE id and lists that folder plus
 * the older name-keyed folders of the task; the older ones are shown under their
 * own heading so a worker can tell a legacy pile from today's work. Upload and
 * delete go through the per-task routes, which the API gates on task scope.
 */
import { useCallback, useEffect, useState } from 'react';
import { listTaskPhotos, uploadTaskPhoto, deleteTaskPhoto } from '@/api/interventie';
import { compressImage } from '@/utils/imageCompression';
import { interventieFaseIdFromKey } from '@/utils/interventiePhotos';
import { isPdfPhoto } from '@/utils/photoFiles';
import { PhotoLightbox } from '@/components/ui/PhotoLightbox';
import { PhotoGridThumb } from '@/components/ui/PhotoGridThumb';
import { SquarePhotoUploadSplitControls } from '@/components/ui/PhotoUploadSplitControls';
import type { PhotoItem } from '@/types/odoo';
import { useMeerwerkFormStore } from '@/stores/meerwerkForm';

interface InterventieTaskPhotoSectionProps {
  taskId: number;
  canUpload?: boolean;
  canDelete?: boolean;
  /** Offer "Uren melden": opens the page's meerwerk form preset to werkuren. Only
   *  where a MeerwerkenSection is on the page and the caller may log meerwerk. */
  urenMelden?: boolean;
}

export function InterventieTaskPhotoSection({ taskId, canUpload = true, canDelete = true, urenMelden = false }: InterventieTaskPhotoSectionProps) {
  const openMeerwerk = useMeerwerkFormStore((s) => s.open);
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ photos: PhotoItem[]; index: number } | null>(null);

  const loadPhotos = useCallback(async () => {
    try {
      setPhotos(await listTaskPhotos(taskId));
    } catch {
      // Silently fail, photos just won't show
    } finally {
      setLoading(false);
    }
  }, [taskId]);

  useEffect(() => {
    setLoading(true);
    void loadPhotos();
  }, [loadPhotos]);

  const handleUpload = async (files: FileList) => {
    setUploading(true);
    setUploadError(null);
    try {
      for (const file of Array.from(files)) {
        await uploadTaskPhoto(taskId, await compressImage(file));
      }
      await loadPhotos();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload mislukt');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (key: string) => {
    setDeleting(key);
    try {
      await deleteTaskPhoto(taskId, key);
      setPhotos((prev) => prev.filter((p) => p.key !== key));
    } catch {
      // silently fail
    } finally {
      setDeleting(null);
    }
  };

  const current = photos.filter((p) => interventieFaseIdFromKey(p.key) !== null);
  const older = photos.filter((p) => interventieFaseIdFromKey(p.key) === null);

  const renderGrid = (list: PhotoItem[], withUpload: boolean) => {
    // PhotoLightbox is an image viewer; a PDF opens in a new tab instead, so it
    // must not appear in the lightbox array or in its index math.
    const images = list.filter((p) => !isPdfPhoto(p));
    return (
      <div className="flex flex-wrap gap-2 items-center">
        {list.map((p) => (
          <div key={p.key} className="relative shrink-0 group">
            <PhotoGridThumb
              photo={p}
              tileSizeClass="w-20 h-20 lg:w-24 lg:h-24"
              imgClassName={`w-20 h-20 lg:w-24 lg:h-24 object-cover rounded-lg border border-gray-200 hover:border-td-blue transition-colors ${deleting === p.key ? 'opacity-40' : ''}`}
              onOpen={() => setLightbox({ photos: images, index: images.findIndex((i) => i.key === p.key) })}
            />
            {canDelete && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); void handleDelete(p.key); }}
                disabled={deleting === p.key}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm hover:bg-red-600 transition-colors"
                aria-label={`Foto ${p.filename} verwijderen`}
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        ))}
        {withUpload && <SquarePhotoUploadSplitControls uploading={uploading} onFilesSelected={handleUpload} />}
      </div>
    );
  };

  return (
    <section aria-label="Interventiefoto's" className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="text-xs font-bold text-td-muted uppercase tracking-wide">Interventiefoto&apos;s</h2>
        {urenMelden && (
          <button
            type="button"
            onClick={() => openMeerwerk('wachturen')}
            className="rounded-lg bg-td-blue/10 px-3 py-1.5 text-xs font-bold text-td-blue hover:bg-td-blue/20 transition-colors min-h-[36px]"
          >
            Uren melden
          </button>
        )}
      </div>

      {loading ? (
        <div className="w-20 h-20 lg:w-24 lg:h-24 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin" role="status">
            <span className="sr-only">Foto&apos;s laden...</span>
          </div>
        </div>
      ) : (
        <>
          {renderGrid(current, canUpload)}
          {!canUpload && current.length === 0 && older.length === 0 && (
            <p className="text-sm text-td-muted">Nog geen interventiefoto&apos;s.</p>
          )}
          {older.length > 0 && (
            <div className="mt-4">
              <h3 className="text-[11px] font-bold text-td-muted uppercase tracking-wide mb-2">
                Oudere interventiefoto&apos;s
              </h3>
              {renderGrid(older, false)}
            </div>
          )}
        </>
      )}

      {uploadError && <p className="text-xs text-red-500 mt-1">{uploadError}</p>}

      {lightbox && lightbox.photos.length > 0 && (
        <PhotoLightbox photos={lightbox.photos} initialIndex={lightbox.index} onClose={() => setLightbox(null)} />
      )}
    </section>
  );
}
